import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Alert, CircularProgress } from '@mui/material';

import TSButton from 'components/common/Button/Button';
import {
  useGetWalkerWelfareCheckQuery,
  useSubmitWalkerWelfareCheckResponseMutation,
} from 'app/api/welfareCheckResponseApiSlice';

const RESPONSE_OPTIONS = [
  { option: 'STILL_PROCEEDING', label: 'Still on the trail' },
  { option: 'RETURNED', label: "I'm back safe" },
];

const NEED_HELP_OPTION = { option: 'NEED_HELP', label: 'I need help' };

// Static, guidance-only advice — see docs/adr/0001. This never auto-escalates,
// it only logs the flag for a Safety Monitor to see.
const NEED_HELP_ADVICE =
  "Trail Safe is not an emergency service. If this is a life-threatening emergency, call 000 now. Flagging this just lets a Safety Monitor know — for anything urgent, contact emergency services directly.";

const formatExpectedEndTime = (isoString) =>
  isoString
    ? new Date(isoString).toLocaleString('en-AU', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';

// Copy is hardcoded with variable interpolation, not database-driven — see
// docs/adr/0005.
const buildStatusText = ({ state, trailName, expectedEndTime }) => {
  switch (state) {
    case 'NOT_DUE':
      return "You're on track — no action needed right now.";
    case 'FIRST_PROMPT_SENT':
      return `You were expected back from ${trailName} by ${expectedEndTime}. Still on the trail, or already home?`;
    case 'URGENT_PROMPT_SENT':
      return `We still haven't heard from you. You were expected back from ${trailName} by ${expectedEndTime} — please let us know you're okay.`;
    case 'ESCALATED':
      return `We haven't heard from you, so we've reached out to your Emergency Contact. You were expected back from ${trailName} by ${expectedEndTime} — you can still update us below.`;
    case 'RESOLVED':
      return "This activity is now closed — you're marked as safe. Nothing further required.";
    default:
      return '';
  }
};

const WalkerResponse = () => {
  const { token } = useParams();
  const { data, isLoading, isError } = useGetWalkerWelfareCheckQuery(token);
  const [submitResponse, { isLoading: isSubmitting }] =
    useSubmitWalkerWelfareCheckResponseMutation();

  const [liveState, setLiveState] = useState(null);
  const [lastSubmitted, setLastSubmitted] = useState(null);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col gap-2">
        <Typography variant="h5" fontWeight={700}>
          This link isn't valid
        </Typography>
        <Typography variant="body1">
          Double check the link from your text message — it may be out of
          date.
        </Typography>
      </div>
    );
  }

  const { need_help_enabled, walker_name, trail_name, expected_end_date } =
    data;
  const state = liveState ?? data.state;
  const expectedEndTime = formatExpectedEndTime(expected_end_date);
  const options = need_help_enabled
    ? [...RESPONSE_OPTIONS, NEED_HELP_OPTION]
    : RESPONSE_OPTIONS;

  const handleRespond = async (option) => {
    const result = await submitResponse({ token, option }).unwrap();
    setLiveState(result.state);
    setLastSubmitted(option);
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="h5" fontWeight={700}>
        Hi {walker_name}
      </Typography>
      <Typography variant="body1">
        {buildStatusText({ state, trailName: trail_name, expectedEndTime })}
      </Typography>

      {state !== 'RESOLVED' && (
        <div className="flex flex-col gap-2">
          {options.map(({ option, label }) => (
            <TSButton
              key={option}
              btnText={label}
              btnVariant={lastSubmitted === option ? 'contained' : 'outlined'}
              onClickFunc={() => handleRespond(option)}
              isLoading={isSubmitting}
            />
          ))}
        </div>
      )}

      {lastSubmitted && (
        <Alert severity="success">
          Got it — thanks, logged as: "
          {options.find((o) => o.option === lastSubmitted)?.label}".
        </Alert>
      )}

      {lastSubmitted === 'NEED_HELP' && (
        <Alert severity="info">{NEED_HELP_ADVICE}</Alert>
      )}
    </div>
  );
};

export default WalkerResponse;
